import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, Eye, FileSpreadsheet, Loader2, Play, RefreshCw, Trash2, UploadCloud } from "lucide-react";
import api from "../api/api";
import { useDialog } from "./DialogProvider";
import { PremiumCard, StatusBadge } from "./ui";
import { getKategoriFromKode } from "../utils/productCode";

function statusTone(status) {
  const s = String(status || "").toUpperCase();
  if (s === "VALID") return "emerald";
  if (s === "WARNING") return "amber";
  if (s === "ERROR") return "rose";
  if (s === "DUPLICATE") return "rose";
  if (s === "PROCESSED") return "sky";
  return "slate";
}

function groupBundles(raw) {
  const map = new Map();
  (raw || []).forEach((r) => {
    const id = Number(r?.bundle_id);
    if (!Number.isInteger(id) || id <= 0) return;
    if (!map.has(id)) map.set(id, { id, name: r.bundle_name || `Bundle #${id}` });
  });
  return Array.from(map.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function isProdukUtama(kode) {
  return getKategoriFromKode(kode) === "PRODUK UTAMA";
}

function ProductThumb({ imageUrl, alt }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";
  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-400">
        IMG
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-12 w-12 shrink-0 rounded-lg border border-slate-200 object-cover"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

export default function MarketplaceImportPanel() {
  const dialog = useDialog();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [bundlesRaw, setBundlesRaw] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ marketplace: "Shopee", source: "Shopee", file: null });

  const bundles = useMemo(() => groupBundles(bundlesRaw), [bundlesRaw]);
  const orders = Array.isArray(session?.orders) ? session.orders : [];

  useEffect(() => {
    if (!open) return;
    api.get("/bom").then((res) => setBundlesRaw(Array.isArray(res.data) ? res.data : [])).catch(() => setBundlesRaw([]));
  }, [open]);

  const setMarketplace = (marketplace) => {
    setForm((prev) => ({
      ...prev,
      marketplace,
      source: marketplace === "Tokopedia" ? "Tokopedia" : "Shopee",
    }));
  };

  const upload = async () => {
    if (!form.file) {
      dialog.toast({ type: "error", title: "Import Marketplace", message: "Pilih file Excel dulu." });
      return;
    }
    const fd = new FormData();
    fd.append("marketplace", form.marketplace);
    fd.append("source", form.source);
    fd.append("file", form.file);
    setBusy(true);
    try {
      const res = await api.post("/api/marketplace-import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setSession(res.data?.session || null);
      dialog.toast({ type: "success", title: "Import Marketplace", message: "File berhasil dibaca." });
    } catch (e) {
      dialog.toast({ type: "error", title: "Import Marketplace", message: e?.response?.data?.message || e.message || "Gagal upload import." });
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    if (!session?.id) return;
    setBusy(true);
    try {
      const res = await api.get(`/api/marketplace-import/sessions/${session.id}`, { noCache: true });
      setSession(res.data || null);
    } finally {
      setBusy(false);
    }
  };

  const updateOrder = async (order) => {
    setBusy(true);
    try {
      const res = await api.put(`/api/marketplace-import/orders/${order.id}`, {
        source: order.source,
        bundle_id: order.bundle_id || null,
        items: order.items || [],
      });
      setSession(res.data?.session || null);
      dialog.toast({ type: "success", title: "Draft Import", message: "Order draft diupdate." });
    } catch (e) {
      dialog.toast({ type: "error", title: "Draft Import", message: e?.response?.data?.message || e.message || "Gagal update order." });
    } finally {
      setBusy(false);
    }
  };

  const deleteOrder = async (order) => {
    const ok = await dialog.confirm(`Hapus draft order ${order.order_number}?`, { title: "Hapus Draft", confirmText: "Hapus", cancelText: "Batal" });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.delete(`/api/marketplace-import/orders/${order.id}`);
      setSession(res.data?.session || null);
    } catch (e) {
      dialog.toast({ type: "error", title: "Draft Import", message: e?.response?.data?.message || e.message || "Gagal hapus order." });
    } finally {
      setBusy(false);
    }
  };

  const patchOrderLocal = (orderId, patch) => {
    setSession((prev) => ({
      ...prev,
      orders: (prev?.orders || []).map((o) => (Number(o.id) === Number(orderId) ? { ...o, ...patch } : o)),
    }));
  };

  const patchItemLocal = (orderId, itemId, patch) => {
    setSession((prev) => ({
      ...prev,
      orders: (prev?.orders || []).map((o) => {
        if (Number(o.id) !== Number(orderId)) return o;
        const items = (o.items || []).map((it) => (Number(it.id) === Number(itemId) ? { ...it, ...patch } : it));
        const mainItems = items.filter((it) => it.kode_barang && isProdukUtama(it.kode_barang));
        return {
          ...o,
          items,
          total_item: mainItems.length,
          total_qty: mainItems.reduce((sum, it) => sum + (Number(it.qty) || 0), 0),
        };
      }),
    }));
  };

  const deleteItemLocal = (orderId, itemId) => {
    setSession((prev) => ({
      ...prev,
      orders: (prev?.orders || []).map((o) => {
        if (Number(o.id) !== Number(orderId)) return o;
        const items = (o.items || []).filter((it) => Number(it.id) !== Number(itemId));
        const mainItems = items.filter((it) => it.kode_barang && isProdukUtama(it.kode_barang));
        return {
          ...o,
          items,
          total_item: mainItems.length,
          total_qty: mainItems.reduce((sum, it) => sum + (Number(it.qty) || 0), 0),
        };
      }),
    }));
  };

  const processValid = async () => {
    if (!session?.id) return;
    const totalInvalid = (session.total_warning || 0) + (session.total_error || 0) + (session.total_duplicate || 0);
    const ok = await dialog.confirm(
      `Process order VALID dan hapus order invalid?\n\n✓ Valid: ${session.total_valid || 0}\n✗ Akan dihapus: ${totalInvalid || 0}`,
      {
        title: "Process Marketplace Import",
        confirmText: "Process & Hapus",
        cancelText: "Batal",
      }
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await dialog.toastPromise(
        api.post(`/api/marketplace-import/sessions/${session.id}/process`, {}),
        {
          loading: "Memproses order valid dan menghapus invalid...",
          success: (r) => {
            const processed = r?.data?.result?.processed_count || 0;
            const deleted = r?.data?.result?.deleted_count || 0;
            if (deleted > 0) {
              return `✓ Proses ${processed} order | ✗ Hapus ${deleted} order invalid`;
            }
            return `✓ Proses ${processed} order`;
          },
          error: (e) => e?.response?.data?.message || e.message || "Process gagal.",
        }
      );
      await refresh();
      return res;
    } finally {
      setBusy(false);
    }
  };

  return (
    <PremiumCard className="p-4 lg:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2 text-base font-semibold text-slate-950">
            <FileSpreadsheet className="h-5 w-5 text-emerald-700" />
            Import Marketplace
          </div>
          <div className="mt-1 text-xs text-slate-500">Upload Excel Shopee/Tokopedia untuk draft stock out massal.</div>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setOpen((v) => !v)}>
          {open ? "Tutup" : "Buka Import"}
        </button>
      </div>

      {open ? (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-[180px_180px_minmax(0,1fr)_auto] md:items-end">
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Marketplace</span>
              <select className="input" value={form.marketplace} onChange={(e) => setMarketplace(e.target.value)} disabled={busy}>
                <option value="Shopee">Shopee</option>
                <option value="Tokopedia">Tokopedia</option>
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Source</span>
              <select className="input" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} disabled={busy}>
                <option value="Shopee">Shopee</option>
                <option value="Tokopedia">Tokopedia</option>
                <option value="Spare">Spare</option>
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Excel File</span>
              <input className="input" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} disabled={busy} />
            </label>
            <button type="button" className="btn btn-primary" onClick={upload} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              Upload
            </button>
          </div>

          {session ? (
            <div className="space-y-3">
              <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="badge border-slate-200 bg-white font-semibold text-slate-700">{session.session_code}</span>
                  <span className="badge border-slate-200 bg-white font-semibold text-slate-700">Rows {session.total_rows || 0}</span>
                  <span className="badge border-slate-200 bg-white font-semibold text-slate-700">Orders {session.total_orders || 0}</span>
                  <span className="badge border-slate-200 bg-white font-semibold text-slate-700">Qty {session.total_qty || 0}</span>
                  <span className="badge border-emerald-200 bg-emerald-50 font-semibold text-emerald-700">VALID {session.total_valid || 0}</span>
                  <span className="badge border-amber-200 bg-amber-50 font-semibold text-amber-800">WARNING {session.total_warning || 0}</span>
                  <span className="badge border-rose-200 bg-rose-50 font-semibold text-rose-700">ERROR {session.total_error || 0}</span>
                  <span className="badge border-rose-200 bg-rose-50 font-semibold text-rose-700">DUPLICATE {session.total_duplicate || 0}</span>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="btn btn-secondary px-3" onClick={refresh} disabled={busy}>
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button type="button" className="btn btn-primary" onClick={processValid} disabled={busy || !Number(session.total_valid)}>
                    <Play className="h-4 w-4" />
                    Process Valid Orders
                  </button>
                </div>
              </div>

              <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <tr>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3">Order</th>
                      <th className="px-3 py-3">Jumlah Pesanan</th>
                      <th className="px-3 py-3">Bundle</th>
                      <th className="px-3 py-3">Source</th>
                      <th className="px-3 py-3 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((order) => {
                      const expanded = expandedId === order.id;
                      return (
                        <Fragment key={order.id}>
                          <tr className={"align-top " + (expanded ? "bg-slate-50/60" : "")}>
                            <td className="px-3 py-3"><StatusBadge tone={statusTone(order.status)}>{order.status}</StatusBadge></td>
                            <td className="px-3 py-3">
                              <div className="font-semibold text-slate-950">{order.order_number}</div>
                              <div className="text-xs text-slate-500">Resi: {order.resi_number || "-"}</div>
                              <div className="mt-1 text-xs text-slate-500">{order.total_item || 0} jenis SKU, {order.total_qty || 0} pcs</div>
                              {order.error_message ? <div className="mt-1 text-xs text-rose-700">{order.error_message}</div> : null}
                            </td>
                            <td className="px-3 py-3">
                              <div className="font-semibold tabular-nums text-slate-950">{order.total_qty || 0} pcs</div>
                              <div className="text-xs text-slate-500">{order.total_item || 0} jenis SKU</div>
                            </td>
                            <td className="px-3 py-3">
                              <select className="input min-w-48" value={order.bundle_id || ""} onChange={(e) => patchOrderLocal(order.id, { bundle_id: e.target.value })} disabled={busy}>
                                <option value="">Tanpa bundle</option>
                                {bundles.map((b) => <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>)}
                              </select>
                              {order.bundle_name ? <div className="mt-1 text-xs text-slate-500">{order.bundle_name}</div> : null}
                            </td>
                            <td className="px-3 py-3">
                              <select className="input min-w-36" value={order.source || "Spare"} onChange={(e) => patchOrderLocal(order.id, { source: e.target.value })} disabled={busy}>
                                <option value="Shopee">Shopee</option>
                                <option value="Tokopedia">Tokopedia</option>
                                <option value="Spare">Spare</option>
                              </select>
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex justify-end gap-2">
                                <button type="button" className="btn btn-secondary px-3" onClick={() => setExpandedId(expanded ? null : order.id)}>
                                  <Eye className="h-4 w-4" />
                                  Detail
                                  <ChevronDown className={"h-4 w-4 transition-transform " + (expanded ? "rotate-180" : "")} />
                                </button>
                                <button type="button" className="btn btn-secondary px-3" onClick={() => updateOrder(order)} disabled={busy || order.status === "PROCESSED"}>Simpan</button>
                                <button type="button" className="btn btn-secondary px-3" onClick={() => deleteOrder(order)} disabled={busy || order.status === "PROCESSED"}><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </tr>
                          {expanded ? (
                            <tr>
                              <td colSpan={6} className="bg-slate-50 px-3 py-3">
                                <div className="rounded-xl border border-slate-200 bg-white p-3">
                                  <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <div className="text-sm font-semibold text-slate-950">Isi Pesanan</div>
                                      <div className="text-xs text-slate-500">Edit SKU/qty atau hapus item yang tidak diperlukan, lalu klik Simpan.</div>
                                    </div>
                                    <StatusBadge tone={statusTone(order.status)}>{order.status}</StatusBadge>
                                  </div>

                                  <div className="overflow-auto rounded-lg border border-slate-200">
                                    <table className="min-w-full text-sm">
                                      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                        <tr>
                                          <th className="px-3 py-2">Foto</th>
                                          <th className="px-3 py-2">SKU / Kode</th>
                                          <th className="px-3 py-2">Qty</th>
                                          <th className="px-3 py-2">Produk Sistem</th>
                                          <th className="px-3 py-2">Variasi</th>
                                          <th className="px-3 py-2">Status</th>
                                          <th className="px-3 py-2 text-right">Aksi</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {(order.items || []).map((it) => (
                                          <tr key={it.id} className="align-top">
                                            <td className="px-3 py-2">
                                              <ProductThumb imageUrl={it.image_url} alt={it.mapped_product_name || it.kode_barang || "Produk"} />
                                            </td>
                                            <td className="px-3 py-2">
                                              <input className="input min-w-36 font-mono" value={it.kode_barang || ""} onChange={(e) => patchItemLocal(order.id, it.id, { kode_barang: e.target.value.toUpperCase() })} disabled={busy || order.status === "PROCESSED"} />
                                            </td>
                                            <td className="px-3 py-2">
                                              <input className="input w-20" type="number" min="1" value={it.qty || 1} onChange={(e) => patchItemLocal(order.id, it.id, { qty: e.target.value })} disabled={busy || order.status === "PROCESSED"} />
                                            </td>
                                            <td className="px-3 py-2 min-w-56 text-slate-700">
                                              <div className="font-semibold">{it.mapped_product_name || "Belum ditemukan"}</div>
                                              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                <span className="font-mono">{it.kode_barang || "-"}</span>
                                                {it.mapped_product_id || it.product_id ? <span>ID {it.mapped_product_id || it.product_id}</span> : null}
                                              </div>
                                            </td>
                                            <td className="px-3 py-2 min-w-40 text-slate-600">{it.variation_name || "-"}</td>
                                            <td className="px-3 py-2">
                                              <StatusBadge tone={statusTone(it.status)}>{it.status}</StatusBadge>
                                              {it.error_message ? <div className="mt-1 text-xs text-rose-700">{it.error_message}</div> : null}
                                            </td>
                                            <td className="px-3 py-2">
                                              <div className="flex justify-end gap-1">
                                                <button type="button" className="btn btn-secondary px-2 py-1 text-xs" onClick={() => updateOrder(order)} disabled={busy || order.status === "PROCESSED"}>Simpan</button>
                                                <button type="button" className="btn btn-secondary px-2 py-1 text-xs text-rose-700 hover:bg-rose-50" onClick={() => deleteItemLocal(order.id, it.id)} disabled={busy || order.status === "PROCESSED"}>Hapus</button>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </PremiumCard>
  );
}
